import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router-dom";
import Resources from "../../../data/services/resources";
import {Field, FieldsManager} from "../../../data/services/fields";
import {getUser} from "../../../common/util/util-auth";
import {DEFAULT_QUERY_LIMIT} from "../../../common/util/util-consts";
import {classNames} from "../../../common/util/util-helpers";
import {toFrontDateTime} from "../../../common/util/util-dates";
import ResourcePageTab from "../../../common/components/layout/layout-components/resource-page-tab";
import {deleteResource, getResource} from "../../../data/actions/resource";
import {pushNotification} from "../../../data/actions/ui";
import useQueryNew from "../../../hooks/use-query-new";
import {DEFAULT_METADATA_SELECT_SEARCH_QUERY} from "../../../util/util-constants";
import {RESULT_BADGE_CLASS} from "./dvir-template-utils";
import ModalDefault from "../../../common/components/modal/modal-default";
import ModalConfirm from "../../../common/components/modal/modal-confirm";
import Image from "../../../common/components/image";
import SubmissionInfoCard from "./submission-info-card";

export default function SubmissionsTab({translate, breakpoint, TruckID}) {
    const tableKey = 'DvirSubmissionID';
    const resourcePath = Resources.DvirSubmissions;

    const location = useLocation();
    // TruckID-scoped instances get their own storage key so the truck tab's
    // filters never overwrite the standalone page's.
    const pagePath = location.pathname.replaceAll("/", "") + (TruckID ? "TruckInspections" : "");
    const dispatch = useDispatch();
    const dialogInfoResource = useSelector(state => state.dialogInfoResource);

    const [queryFields, setQueryFields] = useQueryNew(getQueryFields(), pagePath);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [isConfirmArchiveOpen, setIsConfirmArchiveOpen] = useState(false);
    const [enlargedPhotoId, setEnlargedPhotoId] = useState(null);

    const selects = {
        DriverID: {
            api: 'api/' + Resources.DriversQuick,
            query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
            searchMap: (item) => ({value: item.DriverID, label: item.Driver})
        },
        TruckID: {
            api: 'api/' + Resources.TrucksQuick,
            query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
            searchMap: (item) => ({value: item.TruckID, label: item.Truck})
        },
        TrailerID: {
            api: 'api/' + Resources.TrailersQuick,
            query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
            searchMap: (item) => ({value: item.TrailerID, label: item.Trailer})
        },
        DvirTemplateID: {
            // No /quick variant exists for templates; the list endpoint serves the picker.
            api: 'api/' + Resources.DvirTemplates,
            query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
            searchMap: (item) => ({value: item.DvirTemplateID, label: item.TemplateName})
        }
    };

    /**
     * DvirSubmissionController::read() reads these as FLAT query params and builds
     * its own WHERE clause. A `searchFields` blob is silently ignored -- every
     * filter would appear to do nothing. Do not "fix" this to use
     * addQuerySearchFields, despite what the handoff says.
     */
    function getQuery() {
        const query = FieldsManager.getFieldKeyValues(queryFields);

        return TruckID ? Object.assign({}, query, {TruckID: TruckID}) : query;
    }

    function fetchData() {
        dispatch(getResource({
            user: getUser(),
            resource: resourcePath,
            query: getQuery()
        }));
    }

    function handleQueryInputChange(name, value) {
        const queryFieldsUpdate = FieldsManager.updateField(queryFields, name, value);

        if ("sortBy" === name && value === queryFieldsUpdate?.sortBy?.value) {
            queryFieldsUpdate.sort.value = queryFieldsUpdate.sort.value === 'ASC' ? 'DESC' : 'ASC';
        }

        if ("query" === name || "limit" === name) {
            queryFieldsUpdate.offset.value = 0;
        }

        setQueryFields(queryFieldsUpdate);
    }

    function handleClearAllFiltersClick() {
        setQueryFields(getQueryFields());
    }

    function handleViewClick(item) {
        setSelectedItem(item);
        setIsInfoModalOpen(true);
    }

    function handleDeleteClick(item) {
        setSelectedItem(item);
        setIsConfirmArchiveOpen(true);
    }

    function handleConfirmArchiveClick() {
        dispatch(deleteResource({
            user: getUser(),
            resource: resourcePath,
            piggyResource: resourcePath,
            query: Object.assign({id: selectedItem[tableKey]}, getQuery()),
            successMessage: translate('message.dvir_submission_archived'),
            onResultCallback: (result) => {
                if (result?.status === 0) {
                    return; // successMessage covers the happy path
                }

                // Own the failure here rather than letting the saga notify via its
                // errorMessage flag -- that toast is titled with the raw backend
                // code ("ERROR"/"VALIDATION"/"API_ERROR"), untranslated.
                dispatch(pushNotification({
                    title: translate('text.agent_generic_error'),
                    messageType: 'error',
                    timeout: true
                }));
            }
        }));

        setIsConfirmArchiveOpen(false);
        setSelectedItem(null);
    }

    function getQueryFields() {
        return {
            query: new Field('query', '', [''], false, 'search'),
            DriverID: new Field('DriverID', '', [''], false, 'select-search', {}, {isClearable: true}),
            TruckID: new Field('TruckID', '', [''], false, 'select-search', {}, {isClearable: true}),
            TrailerID: new Field('TrailerID', '', [''], false, 'select-search', {}, {isClearable: true}),
            DvirTemplateID: new Field('DvirTemplateID', '', [''], false, 'select-search', {}, {isClearable: true}),
            Result: new Field('Result', '', [''], false, 'select', {}, {
                isClearable: true,
                values: {
                    passed: translate('text.dvir_result_passed'),
                    defects: translate('text.dvir_result_defects'),
                    na: translate('text.dvir_result_na')
                }
            }),
            HasDefects: new Field('HasDefects', '', [''], false, 'select', {}, {
                isClearable: true,
                values: {1: translate('text.yes'), 0: translate('text.no')}
            }),
            DateFrom: new Field('DateFrom', '', [''], false, 'date', {}, {isClearable: true}),
            DateTo: new Field('DateTo', '', [''], false, 'date', {}, {isClearable: true}),
            sort: new Field('sort', 'DESC', ['']),
            sortBy: new Field('sortBy', 'SubmittedAt', ['']),
            offset: new Field('offset', '0', ['']),
            limit: new Field('limit', DEFAULT_QUERY_LIMIT, [''], false, 'select', {
                hideLabel: true,
                labelType: "float"
            }, {menuPlacement: "top", hasPortal: true})
        };
    }

    function getFields() {
        return {
            SubmittedAt: new Field('SubmittedAt', '', [''], false, 'custom', {
                render: (item) => toFrontDateTime(item.SubmittedAt)
            }),
            DriverName: new Field('DriverName', '', [''], false, 'text'),
            TruckNumber: new Field('TruckNumber', '', [''], false, 'text', {hideTable: !!TruckID}),
            TrailerNumber: new Field('TrailerNumber', '', [''], false, 'text'),
            TemplateNameSnapshot: new Field('TemplateNameSnapshot', '', [''], false, 'text'),
            Result: new Field('Result', '', [''], false, 'custom', {
                render: (item) => {
                    const resultKey = ['passed', 'defects', 'na'].includes(item.Result) ? item.Result : 'na';

                    return (
                        <span className={classNames(
                            "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
                            RESULT_BADGE_CLASS[resultKey]
                        )}>
                            {translate('text.dvir_result_' + resultKey)}
                        </span>
                    );
                }
            }),
            DefectCount: new Field('DefectCount', '', [''], false, 'custom', {
                render: (item) => (
                    <span className={item.HasDefects ? "text-red-600 font-semibold" : undefined}>
                        {item.DefectCount ?? 0}
                    </span>
                )
            })
        };
    }

    useEffect(() => {
        fetchData();
    }, [queryFields, TruckID]);

    return (
        <React.Fragment>
            <ResourcePageTab
                tableKey={tableKey}
                pagePath={pagePath}
                resourcePath={resourcePath}
                tableFields={getFields()}
                queryFields={queryFields}
                onQueryInputChange={handleQueryInputChange}
                onClearAllFiltersClick={handleClearAllFiltersClick}
                fetchData={fetchData}
                translate={translate}
                breakpoint={breakpoint}
                selects={selects}
                onView={handleViewClick}
                onRowClick={handleViewClick}
                onDelete={handleDeleteClick}
                tableFiltersExcludedFields={TruckID ? ["TruckID"] : undefined}
                noRecordsText={translate('text.dvir_no_submissions')}
                tableDefaults={{
                    behaviour: {canAdjustWidth: true, hasMenu: true},
                    style: {condensed: true, isGPUAccelerated: true},
                    version: "1"
                }}
            />

            <ModalDefault
                show={isInfoModalOpen}
                title={translate('modal_heading.dvir_submission_detail')}
                widthClass="max-w-3xl"
                translate={translate}
                onClose={() => {
                    setIsInfoModalOpen(false);
                    setSelectedItem(null);
                    setEnlargedPhotoId(null);
                }}
                closeButtonLabel={translate('btn.close')}
            >
                {!!selectedItem && (
                    <SubmissionInfoCard
                        DvirSubmissionID={selectedItem[tableKey]}
                        translate={translate}
                        dispatch={dispatch}
                        dialogInfoResource={dialogInfoResource}
                        onPhotoClick={setEnlargedPhotoId}
                    />
                )}
            </ModalDefault>

            <ModalDefault
                show={!!enlargedPhotoId}
                title={translate('modal_heading.dvir_submission_detail')}
                widthClass="max-w-5xl"
                translate={translate}
                onClose={() => setEnlargedPhotoId(null)}
                closeButtonLabel={translate('btn.close')}
            >
                {!!enlargedPhotoId && (
                    <div className="p-4 flex justify-center">
                        <Image
                            resourcePath={Resources.DvirSubmissionsPhoto}
                            params={{id: enlargedPhotoId}}
                            className="max-h-[70vh] object-contain"
                        />
                    </div>
                )}
            </ModalDefault>

            <ModalConfirm
                show={isConfirmArchiveOpen}
                title={translate('modal_heading.confirm')}
                text={translate('message.dvir_confirm_archive_submission')}
                buttonLabel={translate('btn.confirm')}
                closeButtonLabel={translate('btn.cancel')}
                translate={translate}
                onConfirm={handleConfirmArchiveClick}
                onClose={() => {
                    setIsConfirmArchiveOpen(false);
                    setSelectedItem(null);
                }}
            />
        </React.Fragment>
    );
}
